import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Autocomplete,
  Avatar,
  Badge,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  List,
  ListSubheader,
  Popover,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  SvgIcon,
} from '@mui/material';
import { keyframes } from '@mui/system';
import { alpha } from '@mui/material/styles';
import LanternalLogo from 'src/components/LanternalLogo';
import useAuth from 'src/hooks/useAuth';
import {
  ALERT_BADGE_PULSE_INTERVAL,
  CONSOLE_CLIENT_ATTRIBUTION_GROUP,
  CONSOLE_DEVELOPERS_GROUP,
  CONSOLE_FINANCE_GROUP,
  CONSOLE_GROWTH_GROUP,
} from 'src/constants';
import { getApiEnvironment, isLocalEnvironment, isTestEnvironment } from 'src/utilities/api-config';
import useSettings from 'src/hooks/useSettings';
import { THEMES } from 'src/constants';
import { capitalCase } from 'change-case';
import { AlertsDisplay, useAlerts } from 'src/components/Alerts';
import { useSnackbar } from 'notistack';
import EventNoteIcon from '@mui/icons-material/EventNote';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import FlagIcon from '@mui/icons-material/Flag';
import HandymanIcon from '@mui/icons-material/Handyman';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import HubIcon from '@mui/icons-material/Hub';
import LinkIcon from '@mui/icons-material/Link';
import PersonIcon from '@mui/icons-material/Person';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SettingsIcon from '@mui/icons-material/Settings';
import StorageIcon from '@mui/icons-material/Storage';
import SyncIcon from '@mui/icons-material/Sync';
import UpdateIcon from '@mui/icons-material/Update';
import UploadIcon from '@mui/icons-material/Upload';
import LinkOffOutlinedIcon from '@mui/icons-material/LinkOffOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LaunchIcon from '@mui/icons-material/Launch';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { useRole } from 'src/contexts/RoleContext';
import { useSelectedClients } from 'src/contexts/SelectedClientsContext';
import { renderNavItems } from 'src/utilities/NavBarUtilities';
import { AdminPanelSettingsRounded, LanRounded, ManageHistoryRounded, PeopleRounded, WorkRounded } from '@mui/icons-material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import { NavItemStatusProvider, useNavItemStatuses } from 'src/contexts/NavItemStatusContext';
import { BUILD_INFO } from 'src/buildInfo';
import * as LanternApi from 'src/utilities/LanternApiClient';
import { isPitchContract } from 'src/utilities/ContractUtilities';
import { equalsIgnoreCase } from 'src/utilities/StringUtilities';
import {
  FORECAST_STATUS_KEY,
  CLIENT_ATTRIBUTION_CONTACTS_STATUS_KEY,
  FORECAST_ADMIN_JOB_STATUS_KEY,
  getSections,
} from './sections';
import ForecastNavContextMenu from './ForecastNavContextMenu';
import { drawerWidth } from '../context';

const MondayIcon = (props) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <path d="M4 20V4h4l4 8 4-8h4v16h-3V9l-5 11-5-11v11z" />
  </SvgIcon>
);

const STATUS_REFRESH_INTERVAL_MS = 30 * 60 * 1000;
const FORECAST_STATUS_GENERIC_MESSAGE = 'One or more managed services need attention.';
const FORECAST_STATUS_ICONS = {
  missingNetsuiteId: LinkOffOutlinedIcon,
  missingServiceType: CategoryOutlinedIcon,
  multiple: ReportProblemOutlinedIcon,
  generic: InfoOutlinedIcon,
};

// Pulse animation for alert badge - pulses briefly every ALERT_BADGE_PULSE_INTERVAL seconds
// Uses modern 'scale' property to avoid interfering with MUI's transform positioning
const createPulseAnimation = (intervalSeconds) => {
  const pulseDuration = 1; // 1 second pulse
  const pulsePercent = (pulseDuration / intervalSeconds) * 100;
  const startPulse = 100 - pulsePercent;
  const midPulse = 100 - pulsePercent * 0.5;

  return keyframes`
    0%, ${startPulse}% {
      scale: 1;
      box-shadow: 0 0 0 0 rgba(244, 67, 54, 0);
    }
    ${midPulse}% {
      scale: 1.2;
      box-shadow: 0 0 0 2px rgba(244, 67, 54, 0.4);
    }
    100% {
      scale: 1;
      box-shadow: 0 0 0 0 rgba(244, 67, 54, 0);
    }
  `;
};

const pulse = createPulseAnimation(ALERT_BADGE_PULSE_INTERVAL);

const getSelectedClientId = (selectedClients) => {
  if (!selectedClients || selectedClients.length !== 1) {
    return null;
  }

  return selectedClients[0]?.id ?? selectedClients[0]?.parentClientId ?? null;
};

const buildForecastStatusResolver = (selectedClients, canCheckForecastStatus) => ({
  key: FORECAST_STATUS_KEY,
  resolve: async () => {
    if (!canCheckForecastStatus) {
      return null;
    }

    const selectedId = getSelectedClientId(selectedClients);
    if (!selectedId) {
      return null;
    }

    try {
      const resp = await LanternApi.getForecastRoundtableContractsMissingNetsuiteId(selectedId);
      if (!resp?.success) {
        return null;
      }

      if (!resp.body) {
        return null;
      }

      return {
        type: 'warning',
        blocked: false,
        tooltip: FORECAST_STATUS_GENERIC_MESSAGE,
        icon: FORECAST_STATUS_ICONS.generic,
      };
    } catch (e) {
      console.error('Forecast status check failed:', e);
      return { type: 'warning', blocked: false, tooltip: 'Unable to evaluate status right now' };
    }
  },
});

const buildRequiresClientSelectionResolver = (selectedClients) => ({
  key: 'requiresClientSelection',
  resolve: async () => {
    if (!selectedClients || selectedClients.length !== 1) {
      return { type: 'error', blocked: true, tooltip: 'Select a client to enable' };
    }
    return null;
  },
});

const buildClientPendingResolver = (selectedClients, canCheckClientAttribution) => ({
  key: 'clientPending',
  resolve: async () => {
    if (!canCheckClientAttribution) {
      return null;
    }

    const selectedId = getSelectedClientId(selectedClients);
    if (!selectedId) {
      return null;
    }

    try {
      const resp = await LanternApi.getClientAttributionParentClientWithClientContracts(selectedId);
      if (!resp?.success) return null;
      const parentClientId = resp.body?.parentClient?.parentClientId;
      if (!parentClientId) {
        return {
          type: 'warning',
          blocked: true,
          tooltip: 'Client onboarding is pending. This page will be available once Finance activates the client.',
        };
      }
      return null;
    } catch (e) {
      return null;
    }
  },
});

const buildDataIntegrationsClientPendingResolver = (selectedClients, canCheckClientAttribution) => ({
  key: 'dataIntegrationsClientPending',
  resolve: async () => {
    if (!canCheckClientAttribution) {
      return null;
    }

    const selectedId = getSelectedClientId(selectedClients);
    if (!selectedId) {
      return null;
    }

    try {
      const resp = await LanternApi.getClientAttributionParentClientWithClientContracts(selectedId);
      if (!resp?.success) return null;

      const hasActivePitchContract = (resp.body?.clientContracts || []).some(
        (contract) => isPitchContract(contract) && !equalsIgnoreCase(contract?.serviceStatus ?? '', 'Inactive'),
      );
      if (hasActivePitchContract) {
        return null;
      }

      const parentClientId = resp.body?.parentClient?.parentClientId;
      if (!parentClientId) {
        return {
          type: 'warning',
          blocked: true,
          tooltip: 'Client onboarding is pending. This page will be available once Finance activates the client.',
        };
      }
      return null;
    } catch (e) {
      return null;
    }
  },
});

const buildClientAttributionContactsResolver = (selectedClients, canCheckClientAttribution) => ({
  key: CLIENT_ATTRIBUTION_CONTACTS_STATUS_KEY,
  resolve: async () => {
    if (!canCheckClientAttribution) {
      return null;
    }

    const selectedId = getSelectedClientId(selectedClients);
    if (!selectedId) {
      return null;
    }

    try {
      const resp = await LanternApi.getContactsByParentClientId(selectedId, true);
      if (!resp?.success) return null;
      const contacts = resp.body || [];
      if (contacts.length === 0) {
        return {
          type: 'warning',
          blocked: false,
          tooltip: 'No parent-level contacts have been added for this client.',
        };
      }
      return null;
    } catch (e) {
      return null;
    }
  },
});

const buildBaselineRefreshStatusResolver = (allowProcessAi) => ({
  key: FORECAST_ADMIN_JOB_STATUS_KEY,
  resolve: async () => {
    if (!allowProcessAi) {
      return null;
    }

    try {
      const resp = await LanternApi.getBaselineRefreshStatus();
      if (!resp?.success || !resp.body) {
        return null;
      }

      const { hasFailures, severity, tooltip } = resp.body;
      if (!hasFailures) {
        return null;
      }

      return {
        type: severity === 'error' ? 'error' : 'warning',
        blocked: false,
        tooltip: tooltip || 'One or more baseline refreshes failed.',
      };
    } catch (e) {
      console.error('Baseline refresh status check failed:', e);
      return null;
    }
  },
});

// Template for adding a new status-driven nav item:
// 1) Give the nav item a statusKey in getSections (e.g., statusKey: 'client-data-checks').
// 2) Add a resolver here:
// const buildClientDataStatusResolver = (deps) => ({
//   key: 'client-data-checks',
//   resolve: async () => {
//     // call API / evaluate client selection, return { type: 'warning'|'error', tooltip, blocked? } or null when OK
//     return null;
//   },
// });
// 3) Add the resolver to the array in NavBar (see resolvers below). The status will auto-inject via renderNavItems.

function NavBarContent({ onNavClose, openMobile, routes }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user, logout, hasPermission, hasProcessAIPermission } = useAuth();
  const { selectedRole, setSelectedRole, ROLES } = useRole();
  const { availableClients, selectedClients, setSelectedClients, loadingAvailableClients } = useSelectedClients();
  const { enqueueSnackbar } = useSnackbar();
  const { settings, saveSettings } = useSettings();
  const [settingsValues, setSettingsValues] = React.useState({
    direction: settings.direction,
    responsiveFontSizes: settings.responsiveFontSizes,
    theme: settings.theme,
  });
  const handleSettingsChange = (field, value) => setSettingsValues((v) => ({ ...v, [field]: value }));
  const handleSaveSettings = () => saveSettings(settingsValues);
  const handleCopySecurityGroups = async () => {
    const groups = user?.groups || [];
    if (!groups.length) {
      enqueueSnackbar('No security groups found to copy.', { variant: 'warning' });
      return;
    }

    const uniqueGroups = Array.from(new Set(groups)).sort((a, b) => a.localeCompare(b));
    const payload = [
      `User: ${user?.displayName || ''}`,
      `Email: ${user?.userPrincipalName || ''}`,
      `Groups (${uniqueGroups.length}):`,
      ...uniqueGroups.map((group) => `- ${group}`),
    ]
      .filter(Boolean)
      .join('\n');

    try {
      await navigator.clipboard.writeText(payload);
      enqueueSnackbar('Security groups copied to clipboard.', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Unable to copy security groups.', { variant: 'error' });
    }
  };

  // Alerts
  const { alerts, archivedAlerts, archiveAlert, unarchiveAlert, isArchiving } = useAlerts(isAuthenticated);

  const [userMenuOpen, setUserMenuOpen] = React.useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = React.useState(null);
  const [userMenuTab, setUserMenuTab] = React.useState('settings');
  const [isClientSelectorOpen, setIsClientSelectorOpen] = React.useState(false);

  const { statuses } = useNavItemStatuses();
  const allowProcessAi = Boolean((hasProcessAIPermission && hasProcessAIPermission()) || hasPermission(CONSOLE_DEVELOPERS_GROUP));

  const roleOptions = Object.values(ROLES);

  const allClientsOption = React.useMemo(() => ({ id: 0, name: 'Select Client' }), []);
  const loadingOption = React.useMemo(() => ({ id: -1, name: 'Loading...' }), []);
  const clientOptions = React.useMemo(
    () => [allClientsOption, ...(availableClients || [])],
    [allClientsOption, availableClients],
  );

  // Show loading option during initial load, otherwise show selected client or "Select Client"
  const selectedClientValue = React.useMemo(() => {
    if (loadingAvailableClients && availableClients.length === 0) {
      return loadingOption;
    }
    return selectedClients && selectedClients.length === 1 ? selectedClients[0] : allClientsOption;
  }, [loadingAvailableClients, availableClients.length, selectedClients, loadingOption, allClientsOption]);

  const content = (
    <Box height="100%" display="flex" flexDirection="column">
      {/* Top anchored header */}
      <Box px={2} pt={1} pb={0.5} sx={{ borderBottom: (t) => `1px solid ${t.palette.divider}` }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} width={1}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="start"
            gap={1}
            onClick={() => navigate('/')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') navigate('/');
            }}
            role="button"
            tabIndex={0}
            sx={{ cursor: 'pointer', userSelect: 'none', minWidth: 0 }}
          >
            <LanternalLogo environment={getApiEnvironment()} />
            <Typography variant="h4" color="text.primary" sx={{ fontWeight: 900, textAlign: 'center', marginLeft: 1.5 }}>
              Console
            </Typography>
            {(isLocalEnvironment() || isTestEnvironment()) && (
              <Chip
                label={isLocalEnvironment() ? 'Local' : 'Test'}
                size="small"
                color={isLocalEnvironment() ? 'info' : 'warning'}
                sx={{
                  fontWeight: 'bold',
                  fontSize: '0.7rem',
                  height: '20px',
                }}
              />
            )}
          </Box>
          <Tooltip title="Collapse navigation">
            <IconButton
              size="small"
              onClick={onNavClose}
              sx={{
                ml: 1,
              }}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        {/* Role selector moved to settings popover */}
        {/* Client selector in header (single-select with Select Client) */}
        <Box sx={{ my: 2 }}>
          <Tooltip title={selectedClientValue?.name || ''} arrow placement="right">
            <Box>
              <Autocomplete
                options={clientOptions}
                value={selectedClientValue}
                getOptionLabel={(option) => option?.name || ''}
                isOptionEqualToValue={(opt, val) => (opt?.id ?? null) === (val?.id ?? null)}
                onOpen={() => setIsClientSelectorOpen(true)}
                onClose={() => setIsClientSelectorOpen(false)}
                onChange={(e, newValue) => {
                  if (!newValue || newValue.id === 0) {
                    setSelectedClients([]); // Select Client (no filter)
                  } else {
                    setSelectedClients([newValue]);
                  }
                }}
                loading={loadingAvailableClients}
                disabled={loadingAvailableClients && availableClients.length === 0}
                renderInput={(params) => <TextField {...params} variant="outlined" placeholder="Select client" />}
                slotProps={{
                  popper: {
                    sx: {
                      '& .MuiAutocomplete-paper': {
                        border: '1px solid',
                        borderColor: 'divider',
                        boxShadow: (theme) => theme.shadows[10],
                        backgroundColor: 'background.paper',
                        mt: 0.5,
                      },
                      '& .MuiAutocomplete-listbox': {
                        py: 0.5,
                      },
                      '& .MuiAutocomplete-option': {
                        minHeight: 36,
                        fontSize: '0.86rem',
                        borderBottom: '1px solid',
                        borderColor: (theme) => alpha(theme.palette.divider, 0.65),
                        '&[aria-selected="true"]': {
                          backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.08),
                        },
                        '&.Mui-focused': {
                          backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.12),
                        },
                        '&:hover': {
                          backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.1),
                        },
                      },
                    },
                  },
                }}
                sx={{
                  '& .MuiInputBase-input:not(:placeholder-shown)': {
                    fontWeight: 700,
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderWidth: 3,
                  },
                }}
              />
            </Box>
          </Tooltip>
        </Box>
      </Box>

      {/* Middle scrollable section */}
      <Box
        pt={1}
        px={2}
        pb={2}
        sx={{
          overflow: 'auto',
          flex: 1,
          filter: isClientSelectorOpen ? 'blur(2px)' : 'none',
          opacity: isClientSelectorOpen ? 0.65 : 1,
          pointerEvents: isClientSelectorOpen ? 'none' : 'auto',
          transition: 'filter 140ms ease, opacity 140ms ease',
        }}
      >
        {(() => {
          const sections = getSections(
            routes,
            selectedClients,
            allowProcessAi,
            {
              WorkRounded,
              EventNoteIcon,
              PeopleRounded,
              HubIcon,
              DescriptionOutlinedIcon,
              LanRounded,
              UpdateIcon,
              AutoGraphIcon,
              FlagIcon,
              StorageIcon,
              PersonAddAlt1Icon,
              PersonIcon,
              AdminPanelSettingsRounded,
              ManageHistoryRounded,
              HandymanIcon,
              SettingsIcon,
              ExitToAppIcon,
              LinkIcon,
              ReceiptLongIcon,
              UploadIcon,
              SyncIcon,
              MondayIcon,
              AssignmentTurnedInIcon,
              HelpOutlineIcon,
            },
            hasPermission(CONSOLE_FINANCE_GROUP),
            <ForecastNavContextMenu />,
          );
          return sections.map((section, idx) => (
            <React.Fragment key={section.subheader || idx}>
              <List
                dense
                disablePadding
                subheader={
                  <ListSubheader
                    disableSticky
                    disableGutters
                    sx={{
                      fontWeight: 'bold',
                      px: 0,
                      py: 0.5,
                      mt: idx === 0 ? 0 : 0.5,
                      mb: 0.5,
                      lineHeight: 1.2,
                      fontSize: 13,
                      color: 'text.secondary',
                    }}
                  >
                    {section.subheader}
                  </ListSubheader>
                }
              >
                {renderNavItems({
                  items: section.items,
                  pathname: location.pathname,
                  expanded: openMobile,
                  statuses,
                })}
                {section.subheader === 'Global' &&
                  renderNavItems({
                    items: [
                      {
                        title: 'Lantern',
                        icon: LaunchIcon,
                        route: {
                          accessible: true,
                          splitTarget: 'content',
                          externalUrl: 'https://lantern.harvestgroup.com',
                          path: 'https://lantern.harvestgroup.com',
                        },
                      },
                    ],
                    pathname: location.pathname,
                    expanded: openMobile,
                    statuses,
                  })}
              </List>
              {idx < sections.length - 1 && (
                <Divider
                  sx={{
                    my: 1,
                    borderBottomWidth: 2,
                    borderColor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)'),
                  }}
                />
              )}
            </React.Fragment>
          ));
        })()}
      </Box>

      {/* Bottom anchored user area */}
      {isAuthenticated && (
        <Box
          px={2}
          py={1.5}
          sx={{
            borderTop: (t) => `1px solid ${t.palette.divider}`,
            filter: isClientSelectorOpen ? 'blur(2px)' : 'none',
            opacity: isClientSelectorOpen ? 0.65 : 1,
            pointerEvents: isClientSelectorOpen ? 'none' : 'auto',
            transition: 'filter 140ms ease, opacity 140ms ease',
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            onClick={(e) => {
              setUserMenuAnchor(e.currentTarget);
              setUserMenuOpen(true);
              // Default to alerts tab if there are alerts, otherwise settings
              setUserMenuTab(alerts.length > 0 ? 'alerts' : 'settings');
            }}
            sx={{ cursor: 'pointer' }}
          >
            <Badge
              color="error"
              badgeContent={alerts.length}
              overlap="circular"
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              sx={
                alerts.length > 0
                  ? {
                      '& .MuiBadge-badge': {
                        animation: `${pulse} ${ALERT_BADGE_PULSE_INTERVAL}s ease-in-out infinite`,
                        transformOrigin: 'center center',
                      },
                    }
                  : {}
              }
            >
              <Avatar src={user?.avatar} alt={user?.displayName} sx={{ width: 36, height: 36 }} />
            </Badge>
            <Box flex={1} minWidth={0}>
              <Typography variant="body1" noWrap>
                {user?.displayName}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {user?.userPrincipalName || ''}
              </Typography>
            </Box>
          </Box>
          <Popover
            open={userMenuOpen}
            onClose={() => setUserMenuOpen(false)}
            anchorEl={userMenuAnchor}
            anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
            slotProps={{ paper: { sx: { p: 2, width: 520 } } }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <Avatar src={user?.avatar} sx={{ width: 32, height: 32 }} />
              <Box>
                <Typography variant="subtitle1">{user?.displayName}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {user?.userPrincipalName}
                </Typography>
              </Box>
            </Box>
            <Tabs value={userMenuTab} onChange={(e, v) => setUserMenuTab(v)}>
              <Tab value="settings" label="Settings" />
              <Tab value="alerts" label={`Alerts (${alerts.length})`} />
            </Tabs>
            <Box mt={2}>
              {userMenuTab === 'settings' && (
                <Box
                  sx={{
                    maxHeight: '330px',
                    minHeight: '330px',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'auto',
                    pt: 1,
                  }}
                >
                  <Box mb={2}>
                    <Autocomplete
                      size="small"
                      value={selectedRole}
                      onChange={(event, newValue) => {
                        if (newValue) setSelectedRole(newValue);
                      }}
                      options={roleOptions}
                      renderInput={(params) => <TextField {...params} label="Role" variant="outlined" />}
                    />
                  </Box>
                  <Box mt={1}>
                    <TextField
                      fullWidth
                      label="Theme"
                      name="theme"
                      onChange={(e) => handleSettingsChange('theme', e.target.value)}
                      select
                      SelectProps={{ native: true }}
                      value={settingsValues.theme}
                      variant="outlined"
                    >
                      {Object.keys(THEMES).map((theme) => (
                        <option key={theme} value={theme}>
                          {capitalCase(theme)}
                        </option>
                      ))}
                    </TextField>
                  </Box>
                  <Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settingsValues.responsiveFontSizes}
                          onChange={(e) => handleSettingsChange('responsiveFontSizes', e.target.checked)}
                        />
                      }
                      label="Responsive font sizes"
                    />
                  </Box>
                  <Divider sx={{ my: 2 }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                      Build Information
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Commit: {BUILD_INFO.gitCommitShortHash} ({BUILD_INFO.gitBranch})
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Build: {BUILD_INFO.buildNumber}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Time: {new Date(BUILD_INFO.buildTimestamp).toLocaleString()}
                    </Typography>
                  </Box>
                  <Box mt="auto" display="flex" justifyContent="flex-end" gap={1}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<ContentCopyIcon fontSize="small" />}
                      onClick={handleCopySecurityGroups}
                    >
                      Copy Security Groups
                    </Button>
                    <Button variant="contained" onClick={handleSaveSettings}>
                      Save Settings
                    </Button>
                    <Button
                      onClick={async () => {
                        try {
                          await logout();
                        } catch {
                          enqueueSnackbar('Unable to logout', { variant: 'error' });
                        }
                      }}
                      variant="contained"
                      color="inherit"
                    >
                      Logout
                    </Button>
                  </Box>
                </Box>
              )}
              {userMenuTab === 'alerts' && (
                <Box
                  sx={{
                    maxHeight: '330px',
                    minHeight: '330px',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'auto',
                    pt: 1,
                  }}
                >
                  <AlertsDisplay
                    alerts={alerts}
                    archivedAlerts={archivedAlerts}
                    archiveAlert={archiveAlert}
                    unarchiveAlert={unarchiveAlert}
                    isArchiving={isArchiving}
                  />
                </Box>
              )}
            </Box>
          </Popover>
        </Box>
      )}
    </Box>
  );

  return (
    <React.Fragment>
      {isAuthenticated && (
        <Box sx={{ display: 'flex' }}>
          <Drawer
            anchor="left"
            sx={[
              {
                width: drawerWidth,
                flexShrink: 0,
                whiteSpace: 'nowrap',
              },
              openMobile
                ? {
                    width: drawerWidth,
                    transition: (theme) =>
                      theme.transitions.create('width', {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.enteringScreen,
                      }),
                  }
                : {
                    transition: (theme) =>
                      theme.transitions.create('width', {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.leavingScreen,
                      }),
                    overflowX: 'hidden',
                    width: 0,
                  },
            ]}
            slotProps={{
              paper: {
                sx: [
                  openMobile
                    ? {
                        width: drawerWidth,
                        transition: (theme) =>
                          theme.transitions.create('width', {
                            easing: theme.transitions.easing.sharp,
                            duration: theme.transitions.duration.enteringScreen,
                          }),
                      }
                    : {
                        transition: (theme) =>
                          theme.transitions.create('width', {
                            easing: theme.transitions.easing.sharp,
                            duration: theme.transitions.duration.leavingScreen,
                          }),
                        overflowX: 'hidden',
                        width: 0,
                      },
                ],
              },
            }}
            open={openMobile}
            variant="permanent"
          >
            {content}
          </Drawer>
        </Box>
      )}
    </React.Fragment>
  );
}

export function NavStatusProvider({ children }) {
  const { selectedClients } = useSelectedClients();
  const { hasPermission, hasProcessAIPermission } = useAuth();

  const allowProcessAi = Boolean((hasProcessAIPermission && hasProcessAIPermission()) || hasPermission(CONSOLE_DEVELOPERS_GROUP));
  const canCheckForecastStatus = allowProcessAi || hasPermission(CONSOLE_FINANCE_GROUP);
  const canCheckClientAttribution = hasPermission(CONSOLE_CLIENT_ATTRIBUTION_GROUP) || hasPermission(CONSOLE_GROWTH_GROUP);

  const forecastResolver = React.useMemo(
    () => buildForecastStatusResolver(selectedClients, canCheckForecastStatus),
    [selectedClients, canCheckForecastStatus],
  );
  const requiresClientSelectionResolver = React.useMemo(
    () => buildRequiresClientSelectionResolver(selectedClients),
    [selectedClients],
  );
  const clientPendingResolver = React.useMemo(
    () => buildClientPendingResolver(selectedClients, canCheckClientAttribution),
    [selectedClients, canCheckClientAttribution],
  );
  const dataIntegrationsClientPendingResolver = React.useMemo(
    () => buildDataIntegrationsClientPendingResolver(selectedClients, canCheckClientAttribution),
    [selectedClients, canCheckClientAttribution],
  );
  const clientAttributionContactsResolver = React.useMemo(
    () => buildClientAttributionContactsResolver(selectedClients, canCheckClientAttribution),
    [selectedClients, canCheckClientAttribution],
  );
  const baselineRefreshStatusResolver = React.useMemo(() => buildBaselineRefreshStatusResolver(allowProcessAi), [allowProcessAi]);
  const resolvers = React.useMemo(
    () => [
      forecastResolver,
      requiresClientSelectionResolver,
      clientPendingResolver,
      dataIntegrationsClientPendingResolver,
      clientAttributionContactsResolver,
      baselineRefreshStatusResolver,
    ],
    [
      forecastResolver,
      requiresClientSelectionResolver,
      clientPendingResolver,
      dataIntegrationsClientPendingResolver,
      clientAttributionContactsResolver,
      baselineRefreshStatusResolver,
    ],
  );

  return (
    <NavItemStatusProvider resolvers={resolvers} dependencies={[selectedClients]} refreshIntervalMs={STATUS_REFRESH_INTERVAL_MS}>
      {children}
    </NavItemStatusProvider>
  );
}

export default function NavBar(props) {
  return <NavBarContent {...props} />;
}
